import { Component, EventEmitter, forwardRef, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { IconComponent } from '@shared/components/ui/icon/icon.component';

@Component({
  selector: 'app-file-selector-input',
  imports: [CommonModule, IconComponent, TranslateModule],
  template: `
    <div class="input-group" [ngStyle]="inputInlineStyle">
      <div class="file-selector-input">
        <input
          (change)="onFileSelected($event)"
          [accept]="accept"
          [placeholder]="selectedAttachment ? selectedAttachment.name : ('No file chosen' | translate)"
          hidden
          id="file-selector"
          [disabled]="disabled"
          type="file"
        />

        <label
          [class.selected]="selectedAttachment"
          for="file-selector"
          [class.size-large]="buttonSize === 'large'"
          [class.size-medium]="buttonSize === 'medium'"
          [class.size-small]="buttonSize === 'small'"
          [class.size-xs]="buttonSize === 'xs'"
        >
          <span>
            <app-icon
              [size]="iconSize"
              [color]="selectedAttachment ? 'var(--white)' : 'var(--text-color-dark)'"
              iconName="paper-clip"
              [marginBottom]="buttonIconMarginBottom"
              [marginTop]="buttonIconMarginTop"
            ></app-icon>
            @if (hideFileName) {
              {{ selectedAttachment ? ('Change File' | translate) : (label | translate) }}
            }
            @if (!hideFileName) {
              {{ selectedAttachment ? selectedAttachment.name : (label | translate) }}
            }
          </span>
          @if (selectedAttachment && !hideClearIcon) {
            <app-icon (click)="clearSelection($event)" color="var(--white)" iconName="close" marginBottom="-5px"></app-icon>
          }
        </label>
      </div>
    </div>
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SimpleFileSelectorInputComponent),
      multi: true,
    },
  ],
})
export class SimpleFileSelectorInputComponent implements ControlValueAccessor {
  selectedAttachment: File | null = null;

  /**
   * Label for the file selector input
   * Default: 'Choose File'
   *
   */
  @Input() label = 'Choose File';

  /**
   * Acceptable file types
   * Default: '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv'
   *
   */
  @Input() accept = '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv';

  /**
   * Whether the file selector input is disabled
   * Default: false
   */
  @Input() disabled = false;

  /**
   * Inline style for input wrapper
   */
  @Input() inputInlineStyle: Record<string, string>;

  /**
   * Whether to hide the file name
   * Default: false
   */
  @Input() hideFileName = false;

  /**
   * Whether to hide the clear icon
   * Default: true
   */
  @Input() hideClearIcon = false;

  /**
   * Predefined size of the button.
   * default medium
   * Example: small | medium | large
   */
  @Input() buttonSize: 'xs' | 'small' | 'medium' | 'large' | 'width-full' = 'medium';

  /**
   * Margin top of the button icon
   * Default: '-5px'
   */
  @Input() buttonIconMarginBottom = '-5px';

  /**
   * Margin top of the button icon
   */
  @Input() buttonIconMarginTop: string;

  /**
   * Size of the icon
   * Example: 16px | 24px | 32px
   */
  @Input() iconSize: string;

  /**
   * Event emitted when a file is selected
   */
  @Output() attachmentSelected: EventEmitter<File> = new EventEmitter<File>();

  onFileSelected(event: any) {
    const files: FileList = event.target.files;
    this.selectedAttachment = files[0];
    this.attachmentSelected.emit(this.selectedAttachment);

    this.onChange(this.selectedAttachment);
    this.onTouched();
  }

  writeValue(value: File | null): void {
    this.selectedAttachment = value;
  }

  registerOnChange(fn: (value: File | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  clearSelection(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.selectedAttachment = null;
    this.attachmentSelected.emit(null);
    this.onChange(null);
  }

  onChange: (value: File | null) => void = () => {
    // this function is supposed to empty or to be available in future
  };

  onTouched: () => void = () => {
    // this function is supposed to empty or to be available in future
  };
}
